import { z } from 'zod';
import { NO_PRICES, PLAN_IDS, PLAN_LIMITS, PRICE_KEYS, PlanConfigSchema, PlanIdSchema, PlanNamesSchema, type PlanPrices } from './plans.js';
import { DiceEffectRulesSchema } from './dice/effects.js';
import { ROOM_ABOUT_MAX, ROOM_PRICE_MAX, RoomFrequencySchema, RoomLanguageSchema, RoomVisualsSchema } from './roomDetails.js';

/** Identificador opaco (nanoid/uuid). */
export const IdSchema = z.string().min(1).max(64);
export type Id = z.infer<typeof IdSchema>;

export const HexColorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/* ------------------------------------------------------------------ atributos */

/* ------------------------------------------------------------------ usuário */

export const UserSchema = z.object({
  id: IdSchema,
  displayName: z.string().min(1).max(40),
  color: HexColorSchema,
  createdAt: z.number().int().nonnegative(),
  /** 0 = sem avatar; muda a cada troca (a imagem fica em /api/avatars/<id>?v=<versão>) */
  avatarVersion: z.number().int().nonnegative().default(0),
});
export type User = z.infer<typeof UserSchema>;

/* ------------------------------------------------------------------ sala */

/**
 * De onde vêm os números dos dados da mesa: do programa ou aleatórios reais do random.org
 * (docs/spec/dice.md, "Fonte do sorteio"). É uma regra de jogo, escolhida pelo mestre.
 */
export const DiceSourceSchema = z.enum(['program', 'random.org']);
export type DiceSource = z.infer<typeof DiceSourceSchema>;

/**
 * Regras de jogo da mesa (as "barrinhas" e as "linhas digitáveis" dos cartões dos
 * jogadores): quantas aparecem, o nome e a cor de cada barra, quem vê os números e se
 * eles aparecem como valores ou porcentagem. Ver docs/spec/game-rules.md. E de onde vêm os
 * números dos dados (`diceSource`), se os dados 3D animam (`diceAnimation`) e o registro das sessões
 * com os relógios (`sessions`).
 */
export const GAME_RULES_LIMITS = { bars: 4, lines: 4, name: 30 } as const;
export const BarRuleSchema = z.object({
  /** vazio = "Barrinha N" */
  name: z.string().trim().max(GAME_RULES_LIMITS.name).default(''),
  /** cor da barra na mesa; null = a cor que o plugin (ou o padrão) der */
  color: HexColorSchema.nullable().default(null),
  /** quem vê os números: todos, ou só mestres e o dono do cartão */
  numbers: z.enum(['everyone', 'gmOwner']).default('everyone'),
  /** números como valor/máximo ou como porcentagem */
  kind: z.enum(['values', 'percent']).default('values'),
});
export type BarRule = z.infer<typeof BarRuleSchema>;
export const LineRuleSchema = z.object({
  /** nome da linha, mostrado ao editar o cartão */
  name: z.string().trim().max(GAME_RULES_LIMITS.name).default(''),
});
export type LineRule = z.infer<typeof LineRuleSchema>;
/**
 * O registro das sessões e os relógios (docs/spec/session-clock.md, "Ligar e desligar"). Sem o
 * registro, nada do relógio nem a aba Sessões; com ele, o mestre escolhe os relógios.
 */
export const SessionRulesSchema = z.object({
  /** registrar as sessões: o relógio, as linhas no chat e a aba Sessões */
  record: z.boolean().default(true),
  /** a pílula na faixa de abas do chat (no celular, a linha embaixo do nome da mesa) */
  chatClock: z.boolean().default(true),
  /** o anel no botão da mesa, na barra da esquerda */
  barClock: z.boolean().default(true),
});
export type SessionRules = z.infer<typeof SessionRulesSchema>;
export const DEFAULT_SESSION_RULES: SessionRules = SessionRulesSchema.parse({});
export const GameRulesSchema = z.object({
  barCount: z.number().int().min(0).max(GAME_RULES_LIMITS.bars).default(GAME_RULES_LIMITS.bars),
  bars: z.array(BarRuleSchema).max(GAME_RULES_LIMITS.bars).default([]),
  lineCount: z.number().int().min(0).max(GAME_RULES_LIMITS.lines).default(GAME_RULES_LIMITS.lines),
  lines: z.array(LineRuleSchema).max(GAME_RULES_LIMITS.lines).default([]),
  /** números dos dados: random.org quando o servidor tem a chave (sem ela, sempre o programa) */
  diceSource: DiceSourceSchema.default('random.org'),
  /**
   * os dados 3D rolam na tela de todos (docs/spec/dice-3d.md, "Resultado depois da animação");
   * desligado, ninguém vê a animação e o resultado aparece no chat na hora
   */
  diceAnimation: z.boolean().default(true),
  /** o registro das sessões e os relógios; as regras gravadas antes deles valem tudo ligado */
  sessions: SessionRulesSchema.default(DEFAULT_SESSION_RULES),
});
export type GameRules = z.infer<typeof GameRulesSchema>;
export const DEFAULT_GAME_RULES: GameRules = GameRulesSchema.parse({});

/**
 * Sala: o que fica gravado.
 *
 * Os campos de descoberta (sistema de RPG, tags, senha, boas-vindas) vieram do
 * comportamento observado no original — a tela "Explorar salas" de lá permite
 * filtrar por sistema, por tags incluídas/excluídas, por sala com senha e por
 * quem está online, então a sala precisa carregar esses dados.
 */
export const RoomSchema = z.object({
  id: IdSchema,
  name: z.string().min(1).max(60),
  ownerId: IdSchema,
  isPrivate: z.boolean(),
  /** sistema de RPG da mesa (ex.: "D&D 5e", "Call of Cthulhu") */
  rpgSystem: z.string().max(60),
  /** complemento/suplemento do sistema (ex.: "SRD 5.1", "Livro Básico") */
  rpgSystemComplement: z.string().max(60),
  /** tags livres para busca e filtro */
  tags: z.array(z.string().min(1).max(24)).max(12),
  /** mensagem mostrada a quem entra */
  welcomeMessage: z.string().max(500),
  /** a sala exige senha para entrar (a senha em si nunca sai do servidor) */
  hasPassword: z.boolean(),
  /** chat moderado: só mestres e quem tem o modo `voice` falam (docs/spec/room-roles.md) */
  moderated: z.boolean().default(false),
  /** mensagem de status mostrada no cartão da sala */
  statusMessage: z.string().max(120).default(''),
  /** imagem de capa e marca do cartão da sala */
  coverUrl: z.string().max(2000).nullable().default(null),
  logoUrl: z.string().max(2000).nullable().default(null),
  /** o dono procura jogadores ("há vagas") */
  haveVacancies: z.boolean().default(false),
  restrictedUnder18: z.boolean().default(false),
  /** como é a mesa (roomDetails.ts): vazio = não informado */
  language: RoomLanguageSchema.default(''),
  frequency: RoomFrequencySchema.default(''),
  visuals: RoomVisualsSchema.default(''),
  payToPlay: z.boolean().default(false),
  price: z.string().max(ROOM_PRICE_MAX).default(''),
  /** "Sobre a mesa": o texto longo da visão geral, em Talemark */
  about: z.string().max(ROOM_ABOUT_MAX).default(''),
  /** histórico do chat ao entrar: tudo, ou só o que a pessoa recebeu (desde a primeira entrada) */
  logAccess: z.enum(['everyone', 'receipt']).default('everyone'),
  /** barrinhas e linhas dos cartões ("Regras de jogo") */
  gameRules: GameRulesSchema.default(DEFAULT_GAME_RULES),
  createdAt: z.number().int().nonnegative(),
});
export type Room = z.infer<typeof RoomSchema>;

/* ------------------------------------------------------------------ presença e papéis */

/**
 * Modos de presença, na ordem do bit no protocolo de referência
 * (docs/spec/room-roles.md). O papel (mestre/jogador/espectador) é derivado.
 */
export const PRESENCE_MODES = ['gm', 'mute', 'blind', 'voice', 'juggernaut', 'player'] as const;
export const PresenceModeSchema = z.enum(PRESENCE_MODES);
export type PresenceMode = z.infer<typeof PresenceModeSchema>;

export const RoomRoleSchema = z.enum(['gm', 'player', 'spectator']);
export type RoomRole = z.infer<typeof RoomRoleSchema>;

/** Modos que se ligam e desligam um por vez, sem mexer no papel. */
export const PRESENCE_FLAGS = ['mute', 'blind', 'voice'] as const satisfies readonly PresenceMode[];
export type PresenceFlag = (typeof PRESENCE_FLAGS)[number];

export function roleOfModes(modes: readonly PresenceMode[]): RoomRole {
  if (modes.includes('gm')) return 'gm';
  if (modes.includes('player')) return 'player';
  return 'spectator';
}

/** Troca o papel preservando mute/blind/voice; o resultado sai em ordem de bit. */
export function withRole(modes: readonly PresenceMode[], role: RoomRole): PresenceMode[] {
  const next = new Set<PresenceMode>(modes.filter((mode) => mode !== 'gm' && mode !== 'player' && mode !== 'juggernaut'));
  if (role !== 'spectator') next.add(role);
  return PRESENCE_MODES.filter((mode) => next.has(mode));
}

export function withFlag(modes: readonly PresenceMode[], flag: PresenceFlag, on: boolean): PresenceMode[] {
  const next = new Set(modes);
  if (on) next.add(flag);
  else next.delete(flag);
  return PRESENCE_MODES.filter((mode) => next.has(mode));
}

/** Uma pessoa presente na sala, como os outros a veem. */
/** Barrinha do cartão do jogador (vida, mana…): valor, máximo e cor. */
export const MemberBarSchema = z.object({
  value: z.number().finite(),
  max: z.number().finite(),
  color: HexColorSchema,
});
export type MemberBar = z.infer<typeof MemberBarSchema>;

export const MEMBER_LIMITS = { bars: 4, lines: 4, lineLength: 120 } as const;
/** cores das barrinhas, na ordem (vida, mana, energia, outra) */
export const MEMBER_BAR_COLORS = ['#d9434f', '#3f7fe0', '#e3b23b', '#4caf50'] as const;

export const RoomMemberSchema = z.object({
  userId: IdSchema,
  /** apelido nesta sala (`/nick`); null = nome da conta */
  nick: z.string().min(1).max(40).nullable(),
  /** versão da foto nesta sala (`/api/rooms/:id/avatars/:userId?v=`); 0 = vale a do perfil */
  avatarVersion: z.number().int().min(0).default(0),
  modes: z.array(PresenceModeSchema).max(PRESENCE_MODES.length),
  /** ficha da biblioteca escolhida como personagem principal (o "personagemPrincipal" do SDK) */
  mainCharacterId: IdSchema.nullable().default(null),
  /** barrinhas do cartão; posição vazia = sem barra (os plugins usam 1 a 4) */
  bars: z.array(MemberBarSchema.nullable()).max(MEMBER_LIMITS.bars).default([]),
  /** linhas de detalhe do cartão (as "linhas editáveis") */
  lines: z.array(z.string().max(MEMBER_LIMITS.lineLength)).max(MEMBER_LIMITS.lines).default([]),
});
export type RoomMember = z.infer<typeof RoomMemberSchema>;

/**
 * Cartão de sala: a visão de UM usuário sobre a sala (descoberta/lista).
 *
 * Separado de `Room` de propósito: `Room` é o que está gravado; o cartão mistura
 * contadores ao vivo (quem está online agora), o voto e o favorito de quem está
 * olhando, e o último acesso. Sem essa separação, o mesmo registro precisaria de
 * valores diferentes por observador dentro do estado da sala.
 */
export const RoomCardSchema = RoomSchema.extend({
  /** quantas pessoas estão na sala neste instante */
  onlineCount: z.number().int().min(0),
  /** há alguém com papel de mestre presente na sala agora */
  ownerOnline: z.boolean(),
  likes: z.number().int().min(0),
  dislikes: z.number().int().min(0),
  /** voto do usuário que está olhando: 0 = nenhum */
  myVote: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
  isFavorite: z.boolean(),
  /** última vez que este usuário entrou na sala */
  lastAccessAt: z.number().int().nonnegative().nullable(),
  /** nome da conta do dono */
  ownerName: z.string().max(40).default(''),
  /** a cor e a versão da foto da conta do dono (o mestre no cartão); 0 = sem foto */
  ownerColor: z.string().max(32).default(''),
  ownerAvatarVersion: z.number().int().min(0).default(0),
  /** quem olha é mestre auxiliar desta sala */
  amIAuxGm: z.boolean().default(false),
  /** quantas vezes foi favoritada (entra na recomendação) */
  favorites: z.number().int().min(0).default(0),
});
export type RoomCard = z.infer<typeof RoomCardSchema>;

export const RoomVoteSchema = z.union([z.literal(-1), z.literal(0), z.literal(1)]);
export type RoomVote = z.infer<typeof RoomVoteSchema>;

export const CreateRoomInputSchema = z.object({
  name: z.string().min(1).max(60),
  isPrivate: z.boolean().default(false),
  rpgSystem: z.string().max(60).default(''),
  rpgSystemComplement: z.string().max(60).default(''),
  tags: z.array(z.string().min(1).max(24)).max(12).default([]),
  welcomeMessage: z.string().max(500).default(''),
  language: RoomLanguageSchema.default(''),
  frequency: RoomFrequencySchema.default(''),
  /** senha opcional de entrada; nunca é devolvida em nenhuma resposta */
  password: z.string().max(120).nullable().default(null),
});
export type CreateRoomInput = z.infer<typeof CreateRoomInputSchema>;

/* ------------------------------------------------------------------ chat */

/** chat comum, sistema, rolagem, fora do personagem, ação (/me) e risada (/rir) */
export const ChatKindSchema = z.enum(['chat', 'system', 'roll', 'ooc', 'action', 'laugh']);
export type ChatKind = z.infer<typeof ChatKindSchema>;

/** "Falar como": narrador ou um NPC com nome (barra de entrada do chat). */
export const ChatPersonaSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('narrator') }),
  z.object({ kind: z.literal('npc'), name: z.string().trim().min(1).max(40) }),
]);
export type ChatPersona = z.infer<typeof ChatPersonaSchema>;

/**
 * Eventos de sistema do log. `userId`/`displayName` da mensagem são de quem
 * agiu; o alvo, quando há, vem no evento.
 */
/**
 * Canal de uma mensagem dentro da mesa. Sem canal = chat principal. `pvt`: conversa
 * privada com uma pessoa (o remetente é o `userId` da mensagem); `group`: conversa
 * privada em grupo (Room_GroupPVT_* no original).
 */
export const ChatChannelSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('pvt'), to: IdSchema }),
  z.object({ kind: z.literal('group'), groupId: IdSchema }),
]);
export type ChatChannel = z.infer<typeof ChatChannelSchema>;

/**
 * Arquivo enviado no chat ("enviar arquivo", arrastar e soltar, colar imagem). A
 * mensagem guarda só o id; o link assinado é pedido na hora de exibir
 * (`GET /api/files/:id/url`), porque links expiram.
 */
export const ChatAttachmentSchema = z.object({
  fileId: IdSchema,
  name: z.string().min(1).max(200),
  mime: z.string().max(120),
  size: z.number().int().nonnegative(),
});
export type ChatAttachment = z.infer<typeof ChatAttachmentSchema>;

/** Grupo de conversa privada numa mesa. */
export const PvtGroupSchema = z.object({
  id: IdSchema,
  roomId: IdSchema,
  createdBy: IdSchema,
  createdAt: z.number().int().nonnegative(),
  /** quem está no grupo agora */
  members: z.array(IdSchema),
});
export type PvtGroup = z.infer<typeof PvtGroupSchema>;

const EventTargetSchema = z.object({ userId: IdSchema, displayName: z.string().min(1).max(40) });

/**
 * Um passo do relógio da sessão (docs/spec/session-clock.md), com o que a linha do chat mostra: o
 * cliente monta o texto no idioma dele e a hora no fuso dele. `at` é quando o passo valeu, no relógio
 * do servidor (a pausa automática volta até a saída do último mestre; o fim pedido numa pausa vale
 * na hora em que ela começou).
 */
const StepMsSchema = z.number().int().nonnegative();
const StepNumberSchema = z.number().int().min(1);
export const GameSessionStepSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('start'), number: StepNumberSchema, at: StepMsSchema }),
  /** `auto`: o servidor pausou porque nenhum mestre ficou na mesa */
  z.object({ kind: z.literal('pause'), number: StepNumberSchema, at: StepMsSchema, auto: z.boolean().default(false) }),
  /** `pauseMs`: quanto durou a pausa que acabou */
  z.object({ kind: z.literal('resume'), number: StepNumberSchema, at: StepMsSchema, pauseMs: StepMsSchema }),
  /** os totais da sessão: jogo, pausas e quantas pausas */
  z.object({ kind: z.literal('end'), number: StepNumberSchema, at: StepMsSchema, playedMs: StepMsSchema, pausedMs: StepMsSchema, pauses: z.number().int().nonnegative() }),
]);
export type GameSessionStep = z.infer<typeof GameSessionStepSchema>;

export const ChatEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('join') }),
  /** `message`: despedida do `/partir` */
  z.object({ type: z.literal('leave'), message: z.string().max(200).optional() }),
  z.object({
    type: z.literal('modes'),
    target: EventTargetSchema,
    added: z.array(PresenceModeSchema),
    removed: z.array(PresenceModeSchema),
  }),
  z.object({ type: z.literal('nick'), from: z.string().min(1).max(40), to: z.string().min(1).max(40) }),
  z.object({ type: z.literal('kick'), target: EventTargetSchema, banned: z.boolean() }),
  z.object({ type: z.literal('moderated'), on: z.boolean() }),
  /** conversa em grupo: criada, alguém convidado (`target`) ou saiu */
  z.object({ type: z.literal('groupCreated') }),
  z.object({ type: z.literal('groupInvited'), target: EventTargetSchema }),
  z.object({ type: z.literal('groupParted') }),
  /** alguém mudou o perfil ou a senha da mesa; `value` ausente = removeu */
  z.object({
    type: z.literal('roomChanged'),
    field: z.enum(['name', 'welcome', 'status', 'tags', 'password', 'gameRules']),
    value: z.string().max(500).optional(),
  }),
  /** o relógio da sessão começou, pausou, voltou ou terminou */
  z.object({ type: z.literal('gameSession'), step: GameSessionStepSchema }),
  /** um mestre limpou o chat da mesa (docs/spec/chat-delete.md) */
  z.object({ type: z.literal('chatCleared') }),
]);
export type ChatEvent = z.infer<typeof ChatEventSchema>;

const CHAT_EVENT_TYPES: ReadonlySet<unknown> = new Set(ChatEventSchema.options.map((option) => option.shape.type.value));

/**
 * O evento de uma mensagem de sistema. Um tipo que este lado não conhece (de um servidor mais novo)
 * é deixado de fora e a linha fica com o `text`: sem isso, uma única linha nova no histórico fazia o
 * cliente antigo recusar o `ready` inteiro. Os tipos conhecidos continuam conferidos à risca.
 */
const ChatMessageEventSchema = z.preprocess(
  (value) => (typeof value === 'object' && value !== null && CHAT_EVENT_TYPES.has((value as { type?: unknown }).type) ? value : undefined),
  ChatEventSchema.optional(),
);

export const ChatMessageSchema = z.object({
  id: IdSchema,
  roomId: IdSchema,
  userId: IdSchema,
  displayName: z.string().min(1).max(40),
  color: HexColorSchema,
  kind: ChatKindSchema,
  text: z.string().min(1).max(2000),
  createdAt: z.number().int().nonnegative(),
  persona: ChatPersonaSchema.optional(),
  /** apelido do autor nesta sala, quando diferente do nome da conta */
  nick: z.string().min(1).max(40).optional(),
  /** conversa privada ou em grupo; ausente = chat principal */
  channel: ChatChannelSchema.optional(),
  /** arquivo compartilhado ("enviar arquivo") */
  attachment: ChatAttachmentSchema.optional(),
  /** mensagens de sistema estruturadas (o cliente monta o texto no idioma dele) */
  event: ChatMessageEventSchema,
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

/* ------------------------------------------------------------------ rolagem de dados */

export const DieResultSchema = z.object({
  sides: z.number().int().positive(),
  value: z.number().int().positive(),
});
export type DieResult = z.infer<typeof DieResultSchema>;

/** os índices dos dados que ficaram de fora (`4d6a3`, `2d20b`) */
const DroppedSchema = z.array(z.number().int().min(0).max(254)).max(254).optional();

const RolledItemSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('dice'), sign: z.union([z.literal(1), z.literal(-1)]), faces: z.number().int().min(1).max(255), results: z.array(z.number().int()), dropped: DroppedSchema }),
  z.object({ kind: z.literal('fudge'), sign: z.union([z.literal(1), z.literal(-1)]), results: z.array(z.union([z.literal(-1), z.literal(0), z.literal(1)])), dropped: DroppedSchema }),
  z.object({ kind: z.literal('value'), sign: z.union([z.literal(1), z.literal(-1)]), value: z.number().int() }),
]);

export const RollDetailSchema = z.object({
  items: z.array(RolledItemSchema).max(300),
  sorted: z.boolean(),
  difficulty: z.number().int().nullable(),
  successes: z.number().int().nullable(),
  outcome: z.enum(['criticalFailure', 'failure', 'success']).nullable(),
  /**
   * de onde vieram os números (docs/spec/dice.md, "Fonte do sorteio"): `random.org` (aleatórios
   * reais) ou `fallback` (a mesa usa o random.org, mas ele não estava disponível, e o programa
   * sorteou). Ausente: o programa, como sempre
   */
  rng: z.enum(['random.org', 'fallback']).optional(),
});
export type RollDetail = z.infer<typeof RollDetailSchema>;

/**
 * A textura dos dados (docs/spec/dice-3d.md, "Textura"): os tipos que o navegador abre, o maior
 * arquivo (cada pessoa da mesa baixa a imagem), o maior lado depois da redução no envio e a faixa do
 * zoom ("Tamanho da estampa": 1 é a imagem inteira em cada face; abaixo, ela se repete).
 */
export const DICE_TEXTURE_LIMITS = {
  types: ['image/png', 'image/jpeg', 'image/webp', 'image/gif'],
  maxBytes: 2 * 1024 * 1024,
  side: 512,
  minScale: 0.25,
  maxScale: 4,
} as const;

/**
 * A aparência dos dados 3D de quem rola (Personalizar dados); quem vê usa a de quem rolou. `texture`
 * é uma imagem do Desk Cloud (`rpgdesk-file:<id>`, a mesma forma de `deskFileRefId`, que não vem de
 * deskCloud.ts porque ele importa daqui), e `textureScale`, o zoom dela. Fora do formato ou da faixa,
 * o campo sai e a rolagem continua.
 */
export const DiceStyleSchema = z.object({
  body: HexColorSchema,
  number: HexColorSchema,
  texture: z
    .string()
    .regex(/^rpgdesk-file:[A-Za-z0-9_-]{1,64}$/)
    .optional()
    .catch(undefined),
  textureScale: z.number().min(DICE_TEXTURE_LIMITS.minScale).max(DICE_TEXTURE_LIMITS.maxScale).optional().catch(undefined),
});
export type DiceStyle = z.infer<typeof DiceStyleSchema>;

export const RollResultSchema = z.object({
  id: IdSchema,
  roomId: IdSchema,
  userId: IdSchema,
  displayName: z.string().max(40),
  color: HexColorSchema,
  expression: z.string().min(1).max(120),
  /** dados comuns, para a bandeja 3D (Fudge fica só no detalhe) */
  dice: z.array(DieResultSchema).max(300),
  modifier: z.number().int(),
  total: z.number().int(),
  label: z.string().max(80).nullable(),
  createdAt: z.number().int().nonnegative(),
  /** resultado completo da rolagem (docs/spec/dice.md); ausente em rolagens antigas */
  detail: RollDetailSchema.optional(),
  /** rolagem feita numa conversa privada ou em grupo */
  channel: ChatChannelSchema.optional(),
  /** referência devolvida a quem pediu (não é gravada) */
  ref: z.string().max(80).optional(),
  /** cores dos dados de quem rolou (só para a animação; não é gravada) */
  style: DiceStyleSchema.optional(),
  /**
   * efeitos especiais de quem rolou (só para a animação; não é gravada). Ausente: o padrão do
   * RPG Desk (`matchDiceEffects`); vazia: sem efeitos
   */
  effects: DiceEffectRulesSchema.optional(),
});
export type RollResult = z.infer<typeof RollResultSchema>;

/** Quantas linhas apagadas o `/audit` mostra (as mais novas). */
export const DELETED_CHAT_LIMIT = 200;

/**
 * Uma linha apagada do chat da mesa, como o `/audit` a mostra (docs/spec/chat-delete.md): o
 * conteúdo, quem apagou e quando; `cleared`, se saiu quando limparam o chat.
 */
export const DeletedChatLineSchema = z.object({
  message: ChatMessageSchema,
  deletedAt: z.number().int().nonnegative(),
  deletedByName: z.string().max(80),
  cleared: z.boolean(),
});
export type DeletedChatLine = z.infer<typeof DeletedChatLineSchema>;

/** O que o `/audit` recebe: as linhas e as rolagens apagadas do mesmo trecho (que se ligam ao resumo pelo autor e pela hora). */
export const DeletedChatSchema = z.object({
  lines: z.array(DeletedChatLineSchema).max(DELETED_CHAT_LIMIT),
  rolls: z.array(RollResultSchema).max(DELETED_CHAT_LIMIT),
});
export type DeletedChat = z.infer<typeof DeletedChatSchema>;

/* ------------------------------------------------------------------ contatos e mensagens */

/**
 * Estado da relação entre dois usuários.
 *
 * Vem do comportamento observado: há solicitação pendente (que pode ser aceita
 * ou rejeitada), amizade, bloqueio e — diferente de "excluir" — **esconder da
 * lista de contatos**, que só afeta quem esconde.
 */
export const ContactStateSchema = z.enum(['pending', 'friend', 'blocked', 'hidden']);
export type ContactState = z.infer<typeof ContactStateSchema>;

export const LastMessageSchema = z.object({
  text: z.string().max(4000),
  at: z.number().int().nonnegative(),
  /** true quando a última mensagem foi enviada por mim */
  fromMe: z.boolean(),
});
export type LastMessage = z.infer<typeof LastMessageSchema>;

/**
 * O status que a pessoa escolhe na foto do painel de amigos (docs/spec/friends.md, "Status"):
 * Online (com o "Ausente" automático), Ocupado (os avisos de mensagem direta calam) ou Offline
 * (os amigos a veem offline). Fica na conta: vale na próxima vez que ela entrar.
 */
export const UserStatusSchema = z.enum(['online', 'busy', 'offline']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

/**
 * Onde um amigo está agora (docs/spec/friends.md): conectado, ausente (sem usar o RPG Desk há
 * `FRIENDS_LIMITS.awayAfterMs`), ocupado (o status que ele escolheu; tira o ausente) e numa mesa.
 * A mesa só tem nome quando é pública e sem senha; nas outras, `inRoom` com `room: null`
 * ("Numa mesa"). Quem escolheu Offline chega como offline.
 */
export const FriendPresenceSchema = z.object({
  online: z.boolean(),
  away: z.boolean().default(false),
  busy: z.boolean().default(false),
  inRoom: z.boolean().default(false),
  room: z.object({ id: IdSchema, name: z.string().min(1).max(60) }).nullable().default(null),
});
export type FriendPresence = z.infer<typeof FriendPresenceSchema>;

export const OFFLINE_PRESENCE: FriendPresence = { online: false, away: false, busy: false, inRoom: false, room: null };

export const ContactViewSchema = z.object({
  user: UserSchema,
  state: ContactStateSchema,
  /** true quando a solicitação partiu de mim */
  outgoing: z.boolean(),
  /** texto que acompanhou o pedido de amizade */
  requestMessage: z.string().max(300).nullable(),
  /** alimenta o grupo "Mensagens recentes" */
  lastMessage: LastMessageSchema.nullable(),
  unreadCount: z.number().int().min(0),
  updatedAt: z.number().int().nonnegative(),
  /** conectado agora (só para amigos); o mesmo que `presence.online`, para clientes antigos */
  online: z.boolean().default(false),
  /** onde o amigo está agora (só para amigos e escondidos) */
  presence: FriendPresenceSchema.default(() => ({ ...OFFLINE_PRESENCE })),
  /** quando o amigo saiu pela última vez (só para amigos e escondidos) */
  lastSeenAt: z.number().int().nonnegative().nullable().default(null),
});
export type ContactView = z.infer<typeof ContactViewSchema>;

/**
 * Uma conversa com quem não é amigo (docs/spec/friends.md, "Mensagens de quem não é amigo"): o
 * grupo "Outras conversas" do painel. Sem presença: ela é só dos amigos.
 */
export const ConversationViewSchema = z.object({
  user: UserSchema,
  lastMessage: LastMessageSchema.nullable(),
  unreadCount: z.number().int().min(0),
});
export type ConversationView = z.infer<typeof ConversationViewSchema>;

export const ContactListSchema = z.object({
  friends: z.array(ContactViewSchema),
  /** pedidos que EU recebi (aceitar/rejeitar) */
  incoming: z.array(ContactViewSchema),
  /** pedidos que EU enviei */
  outgoing: z.array(ContactViewSchema),
  blocked: z.array(ContactViewSchema),
  hidden: z.array(ContactViewSchema),
  /** conversas com quem não é amigo, da mais recente para a mais antiga (vazio num servidor antigo) */
  others: z.array(ConversationViewSchema).default([]),
});
export type ContactList = z.infer<typeof ContactListSchema>;

/**
 * Convite para uma mesa, mandado na conversa ("X te chamou para a mesa"). O nome e a senha são os
 * de agora; `gone`: a mesa foi apagada depois do convite.
 */
export const ImsInviteSchema = z.object({
  roomId: IdSchema,
  roomName: z.string().min(1).max(60),
  hasPassword: z.boolean(),
  gone: z.boolean().default(false),
});
export type ImsInvite = z.infer<typeof ImsInviteSchema>;

export const ImsMessageSchema = z.object({
  id: IdSchema,
  fromId: IdSchema,
  toId: IdSchema,
  text: z.string().min(1).max(4000),
  createdAt: z.number().int().nonnegative(),
  readAt: z.number().int().nonnegative().nullable(),
  /** a mensagem é um convite para uma mesa (o `text` traz o nome dela, para quem não mostra o cartão) */
  invite: ImsInviteSchema.nullable().default(null),
});
export type ImsMessage = z.infer<typeof ImsMessageSchema>;

/**
 * De quem a pessoa recebe mensagens diretas: de todos (o padrão) ou só dos amigos. Com "só amigos",
 * quem recebeu uma mensagem dela ainda consegue responder. Fica na conta.
 */
export const ImsPrivacySchema = z.enum(['everyone', 'friends']);
export type ImsPrivacy = z.infer<typeof ImsPrivacySchema>;

/** As escolhas de privacidade da conta (Configurações → Conta e segurança → Privacidade). */
export const PrivacySchema = z.object({ ims: ImsPrivacySchema });
export type Privacy = z.infer<typeof PrivacySchema>;

/**
 * Se dá para mandar mensagem direta a alguém: `friendsOnly`, a pessoa só recebe de amigos (e nunca
 * escreveu para mim); `blockedByMe`, eu a bloqueei; `unavailable`, ela me bloqueou (sem dizer).
 */
export const ImsAccessSchema = z.enum(['ok', 'friendsOnly', 'blockedByMe', 'unavailable']);
export type ImsAccess = z.infer<typeof ImsAccessSchema>;

/** Com quem é a conversa: vem com o histórico, para a janela de quem não está na lista de amigos. */
export const ImsPeerSchema = z.object({
  user: UserSchema,
  /** a relação do meu lado (null: nenhuma) */
  relation: ContactStateSchema.nullable(),
  access: ImsAccessSchema,
});
export type ImsPeer = z.infer<typeof ImsPeerSchema>;

export const IMS_LIMITS = {
  /** tamanho máximo de uma mensagem direta */
  messageMaxLength: 4000,
  /** quantas mensagens o histórico de uma conversa devolve */
  historyLimit: 200,
  /** mensagens que acompanham um pedido de amizade */
  requestMessageMaxLength: 300,
  /** conversas com quem não é amigo que a lista traz ("Outras conversas") */
  othersMax: 50,
} as const;

/** Tempos e limites do painel "Amigos e chat" (docs/spec/friends.md). */
export const FRIENDS_LIMITS = {
  /** sem mexer no RPG Desk por este tempo, a pessoa fica "Ausente" */
  awayAfterMs: 10 * 60_000,
  /** o "digitando…" é reenviado no máximo a cada… */
  typingResendMs: 3_000,
  /** e some sozinho depois disto sem aviso novo */
  typingTtlMs: 6_000,
  /** amigos chamados de uma vez para a mesa */
  inviteMaxUsers: 30,
} as const;

/* ------------------------------------------------------------------ perfil do usuário */

/**
 * Curtir (1) ou não curtir (-1) uma pessoa; 0 tira o voto. O perfil mostra as duas contagens, sem
 * categoria nem ranking (saíram em 2026-10-30).
 */
export const UserVoteSchema = z.union([z.literal(-1), z.literal(0), z.literal(1)]);
export type UserVote = z.infer<typeof UserVoteSchema>;

/**
 * As estrelas das sessões de jogo (docs/spec/session-clock.md, "Avaliações"): quantas a pessoa recebeu
 * e a média, que só vem com 5 ou mais (com menos, seria a nota de um ou dois, à vista de todos).
 */
export const TableRatingSchema = z.object({ count: z.number().int().min(0), average: z.number().min(1).max(5).nullable() });
export type TableRating = z.infer<typeof TableRatingSchema>;

export const UserProfileSchema = z.object({
  user: UserSchema,
  /** texto livre do próprio usuário */
  bio: z.string().max(1000),
  /** sistemas/gostos de RPG declarados */
  rpgPreferences: z.string().max(500),
  /** horários em que costuma jogar */
  timePreferences: z.string().max(200),
  /**
   * A capa (a faixa do alto do perfil): um endereço da web ou um arquivo do Desk Cloud da pessoa
   * (`rpgdesk-file:<id>`, mostrado por `GET /api/users/:id/cover`). null = a faixa na cor da pessoa.
   */
  cover: z.string().nullable(),
  lastSeenAt: z.number().int().nonnegative().nullable(),
  level: z.number().int().min(1).max(99),
  /** quantas pessoas curtiram */
  approvals: z.number().int().min(0),
  /** quantas pessoas não curtiram */
  disapprovals: z.number().int().min(0),
  /**
   * As estrelas das mesas: as que os mestres deram à pessoa como jogadora e as que os jogadores deram
   * às sessões em que ela foi mestre. Ausente: o servidor ainda não tem as avaliações.
   */
  tableRatings: z.object({ asPlayer: TableRatingSchema, asGm: TableRatingSchema }).optional(),
  /**
   * Probabilidade de a conta ser falsa. Fica `null` enquanto não houver
   * avaliação — o original mostra "não calculado" nesse caso, e nós preferimos
   * dizer isso a inventar um número.
   */
  fakeProbability: z.number().min(0).max(1).nullable(),
  /** meu voto sobre este usuário */
  myVote: UserVoteSchema,
  /** relação entre quem está olhando e o perfil */
  relation: ContactStateSchema.nullable(),
  /** é o meu próprio perfil? */
  isMe: z.boolean(),
});
export type UserProfile = z.infer<typeof UserProfileSchema>;

export const ProfileUpdateSchema = z.object({
  bio: z.string().max(1000).optional(),
  rpgPreferences: z.string().max(500).optional(),
  timePreferences: z.string().max(200).optional(),
  // a capa: o endereço da web ou `rpgdesk-file:<id>` (o formato de deskCloud.ts, que importa este
  // arquivo e por isso não entra aqui); null tira a capa
  cover: z
    .string()
    .trim()
    .max(2000)
    .refine((value) => /^https?:\/\/\S+$/i.test(value) || /^rpgdesk-file:[A-Za-z0-9_-]{1,64}$/.test(value), { message: 'endereço de imagem inválido' })
    .nullable()
    .optional(),
});
export type ProfileUpdate = z.infer<typeof ProfileUpdateSchema>;

/**
 * A capa do perfil vinda do Desk Cloud (docs/spec/friends.md, "Perfil"): os tipos que o navegador
 * mostra, o maior arquivo e o maior lado depois da redução no envio. Os mesmos da capa da mesa.
 */
export const PROFILE_COVER_LIMITS = {
  types: ['image/png', 'image/jpeg', 'image/webp', 'image/gif'],
  maxBytes: 5 * 1024 * 1024,
  side: 1600,
} as const;

export const PROFILE_LIMITS = {
  bio: 1000,
  rpgPreferences: 500,
  timePreferences: 200,
} as const;

/* ------------------------------------------------------------------ arquivos (Desk Cloud) */

// o arquivo em si (`DeskFile`), as pastas e as rotas ficam em deskCloud.ts

export const FILE_LIMITS = {
  name: 200,
  mime: 120,
  /** validade da URL assinada de um arquivo pessoal */
  urlTtlMs: 2 * 60 * 60 * 1000,
  /**
   * Validade da URL de um arquivo ligado a uma mesa. É maior porque cenas antigas gravaram o
   * link como imagem do mapa: um prazo curto quebraria o mapa no meio da sessão. As novas
   * guardam o id do arquivo e pedem o link na hora (docs/spec/desk-cloud.md).
   */
  roomUrlTtlMs: 30 * 24 * 60 * 60 * 1000,
  /** tipos aceitos para exibir direto na página (imagem, áudio e vídeo) */
  inlineMimePrefixes: ['image/', 'audio/', 'video/'] as const,
} as const;


/* ------------------------------------------------------------------ administração */

/**
 * De onde vem o papel de administrador do servidor (docs/spec/admin.md): da lista do servidor
 * (`RPGDESK_ADMINS`, que a tela não muda) ou de uma promoção feita por outro admin (no banco).
 */
export const AdminSourceSchema = z.enum(['env', 'db']);
export type AdminSource = z.infer<typeof AdminSourceSchema>;

export const ADMIN_LIMITS = {
  /** motivo da suspensão */
  reason: 500,
  /** contas por página da lista */
  page: 50,
  /** linhas por página do registro */
  logPage: 100,
} as const;

export const AdminSuspensionSchema = z.object({
  reason: z.string().max(ADMIN_LIMITS.reason),
  byName: z.string().max(40),
  at: z.number().int().nonnegative(),
});
export type AdminSuspension = z.infer<typeof AdminSuspensionSchema>;

/** Uma conta na lista da área de administração. */
export const AdminUserRowSchema = z.object({
  user: UserSchema,
  email: z.string().nullable(),
  lastSeenAt: z.number().int().nonnegative().nullable(),
  online: z.boolean(),
  admin: AdminSourceSchema.nullable(),
  suspension: AdminSuspensionSchema.nullable(),
  roomsOwned: z.number().int().nonnegative(),
  usedBytes: z.number().int().nonnegative(),
  /** o plano que vale agora (docs/spec/planos.md) e o fim dele; null sem prazo (e sempre no Grátis) */
  plan: z.object({ id: PlanIdSchema, expiresAt: z.number().int().nonnegative().nullable() }),
});
export type AdminUserRow = z.infer<typeof AdminUserRowSchema>;

/** O filtro de plano da lista: todos, um plano (o que vale agora) ou `paid` (todos menos o Grátis). */
export const AdminPlanFilterSchema = z.enum(['all', ...PLAN_IDS, 'paid']);
export type AdminPlanFilter = z.infer<typeof AdminPlanFilterSchema>;

/**
 * O plano gravado de uma conta, nos detalhes da administração: o plano (texto, pode ter saído da grade),
 * o prazo, se venceu, a nota interna, quem deu (null se essa conta já foi apagada) e quando.
 */
export const AdminPlanRecordSchema = z.object({
  planId: z.string().max(16),
  expiresAt: z.number().int().nonnegative().nullable(),
  expired: z.boolean(),
  note: z.string().max(PLAN_LIMITS.note),
  grantedByName: z.string().nullable(),
  updatedAt: z.number().int().nonnegative(),
});
export type AdminPlanRecord = z.infer<typeof AdminPlanRecordSchema>;

export const AdminUserFilterSchema = z.enum(['all', 'online', 'admins', 'suspended']);
export type AdminUserFilter = z.infer<typeof AdminUserFilterSchema>;
export const AdminUserSortSchema = z.enum(['recent', 'name', 'seen']);
export type AdminUserSort = z.infer<typeof AdminUserSortSchema>;

/** O que fica no registro: cada ação de um administrador sobre uma conta, uma mesa, um plano ou um plugin oficial. */
export const AdminLogActionSchema = z.enum([
  'suspend',
  'unsuspend',
  'password',
  'delete',
  'promote',
  'demote',
  'plan',
  'plan-config',
  'room-delete',
  'plugin-add',
  'plugin-version',
  'plugin-systems',
  'plugin-hide',
  'plugin-show',
  'plugin-delete',
  'desk-tutor',
]);
export type AdminLogAction = z.infer<typeof AdminLogActionSchema>;

/**
 * O `detail` de uma troca de plano no registro (docs/spec/planos.md), em JSON. Os planos são texto: um
 * deles pode ter saído da grade depois.
 */
export const AdminPlanDetailSchema = z.object({
  /** o plano que valia antes */
  from: z.string().max(16),
  /** o plano novo */
  to: z.string().max(16),
  /** o fim do plano novo; null sem prazo */
  expiresAt: z.number().int().nonnegative().nullable(),
  note: z.string().max(PLAN_LIMITS.note),
});
export type AdminPlanDetail = z.infer<typeof AdminPlanDetailSchema>;

/** Lê o `detail` de uma troca de plano; null se não for o JSON esperado (a tela mostra o texto cru). */
export function parseAdminPlanDetail(detail: string): AdminPlanDetail | null {
  try {
    const parsed = AdminPlanDetailSchema.safeParse(JSON.parse(detail));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Um plano como ficou gravado no registro (antes e depois de uma edição): os direitos como texto solto,
 * porque um direito pode ter saído do código depois.
 */
const LoggedPlanConfigSchema = z
  .object({
    entitlements: z.record(z.string(), z.union([z.number(), z.boolean(), z.null()])),
    names: PlanNamesSchema,
    prices: z.partialRecord(z.enum(PRICE_KEYS), z.number().int().nullable()).optional(),
    /** as linhas de antes de 2026-11-05 têm um preço só, o do Pix por mês */
    priceCents: z.number().int().nullable().optional(),
  })
  .transform(({ entitlements, names, prices, priceCents }) => ({
    entitlements,
    names,
    prices: { ...NO_PRICES, ...(priceCents === undefined ? {} : { pixMonth: priceCents }), ...prices } as PlanPrices,
  }));

/** O `detail` de uma edição de plano no registro (docs/spec/planos.md, "Editar os planos"), em JSON. */
export const AdminPlanConfigDetailSchema = z.object({
  plan: z.string().max(16),
  before: LoggedPlanConfigSchema,
  after: LoggedPlanConfigSchema,
  /** a edição voltou o plano ao padrão do código */
  reset: z.boolean(),
});
export type AdminPlanConfigDetail = z.infer<typeof AdminPlanConfigDetailSchema>;

export function parseAdminPlanConfigDetail(detail: string): AdminPlanConfigDetail | null {
  try {
    const parsed = AdminPlanConfigDetailSchema.safeParse(JSON.parse(detail));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * Um plano na seção Planos da administração: o que vale, o padrão do código, se foi editado (por quem
 * e quando), quantas contas estão nele agora e quantas passam do espaço dele.
 */
export const AdminPlanRowSchema = z.object({
  id: PlanIdSchema,
  config: PlanConfigSchema,
  defaults: PlanConfigSchema,
  edited: z.boolean(),
  updatedAt: z.number().int().nonnegative().nullable(),
  updatedByName: z.string().nullable(),
  accounts: z.number().int().nonnegative(),
  overSpace: z.number().int().nonnegative(),
});
export type AdminPlanRow = z.infer<typeof AdminPlanRowSchema>;

/** Uma mesa na seção Mesas da administração. */
export const AdminRoomRowSchema = z.object({
  id: IdSchema,
  name: z.string(),
  owner: z.object({ id: IdSchema, displayName: z.string() }),
  isPrivate: z.boolean(),
  hasPassword: z.boolean(),
  /** quem já entrou nela */
  members: z.number().int().nonnegative(),
  /** quem está nela agora */
  online: z.number().int().nonnegative(),
  files: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
  createdAt: z.number().int().nonnegative(),
  lastMessageAt: z.number().int().nonnegative().nullable(),
});
export type AdminRoomRow = z.infer<typeof AdminRoomRowSchema>;

export const AdminRoomSortSchema = z.enum(['recent', 'name', 'members', 'space']);
export type AdminRoomSort = z.infer<typeof AdminRoomSortSchema>;

/** O `detail` de uma mesa apagada por um admin, em JSON (o alvo da linha é o dono). */
export const AdminRoomDeleteDetailSchema = z.object({ roomId: z.string(), roomName: z.string() });
export type AdminRoomDeleteDetail = z.infer<typeof AdminRoomDeleteDetailSchema>;

export function parseAdminRoomDeleteDetail(detail: string): AdminRoomDeleteDetail | null {
  try {
    const parsed = AdminRoomDeleteDetailSchema.safeParse(JSON.parse(detail));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * A seção Servidor da administração: a configuração em leitura. Nunca leva segredo (o endereço do
 * banco, as chaves do bucket, o segredo das URLs, a chave do random.org): o servidor monta campo a campo.
 */
export const AdminServerInfoSchema = z.object({
  version: z.string(),
  node: z.string(),
  startedAt: z.number().int().nonnegative(),
  limits: z.object({
    maxUploadBytes: z.number().int().nonnegative(),
    uploadMemoryBytes: z.number().int().nonnegative(),
    uploadMemoryInUse: z.number().int().nonnegative(),
    maxBodyBytes: z.number().int().nonnegative(),
    historyLimit: z.number().int().nonnegative(),
  }),
  /** a pasta dentro do bucket (dev, prod…); null sem bucket (os testes) */
  storagePrefix: z.string().nullable(),
  /** os admins da lista do servidor (RPGDESK_ADMINS), como estão lá */
  admins: z.array(z.string()),
  randomOrg: z.boolean(),
  pluginsUrl: z.string().nullable(),
  corsOrigins: z.array(z.string()),
  /** o servidor serve a interface (RPGDESK_CLIENT_DIR) */
  servesClient: z.boolean(),
});
export type AdminServerInfo = z.infer<typeof AdminServerInfoSchema>;

/** A Visão geral da administração: os números do servidor agora. */
const Count = z.number().int().nonnegative();
export const AdminOverviewSchema = z.object({
  accounts: z.object({ total: Count, newLastWeek: Count, online: Count, suspended: Count, admins: Count }),
  /** as contas em cada plano, pelo plano que vale agora */
  plans: z.record(PlanIdSchema, Count),
  storage: z.object({ usedBytes: Count, files: Count }),
  /** as mesas, e quantas têm alguém dentro agora */
  rooms: z.object({ total: Count, active: Count }),
  /** a memória dos envios em leitura e o teto dela */
  uploads: z.object({ memoryInUse: Count, memoryLimit: Count }),
});
export type AdminOverview = z.infer<typeof AdminOverviewSchema>;

/** Uma linha do registro. Os nomes ficam gravados: a conta apagada continua com nome aqui. */
export const AdminLogEntrySchema = z.object({
  seq: z.number().int().nonnegative(),
  adminId: IdSchema.nullable(),
  adminName: z.string(),
  targetId: IdSchema.nullable(),
  targetName: z.string(),
  action: AdminLogActionSchema,
  detail: z.string(),
  at: z.number().int().nonnegative(),
});
export type AdminLogEntry = z.infer<typeof AdminLogEntrySchema>;

/** Os detalhes de uma conta: a linha da lista e o resto. */
export const AdminUserDetailSchema = AdminUserRowSchema.extend({
  tosVersion: z.number().int().nonnegative(),
  tosAcceptedAt: z.number().int().nonnegative().nullable(),
  /** sessões abertas (cada aparelho ou navegador em que a pessoa entrou) */
  sessions: z.number().int().nonnegative(),
  /** a última vez que entrou com a senha (a sessão mais nova) */
  lastLoginAt: z.number().int().nonnegative().nullable(),
  quotaBytes: z.number().int().nonnegative(),
  /** o plano gravado, vencido ou não; null no Grátis sem linha */
  planRecord: AdminPlanRecordSchema.nullable(),
  files: z.number().int().nonnegative(),
  friends: z.number().int().nonnegative(),
  rooms: z.array(z.object({ id: IdSchema, name: z.string(), createdAt: z.number().int().nonnegative() })),
  history: z.array(AdminLogEntrySchema),
});
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;
